'use client';

import { ReleaseNotesSheetContent } from '@/components/sidebar/shared/floating-menu/components/release-notes/release-notes-sheet-content';
import { useReleaseNoteReadStatus } from '@/components/sidebar/shared/floating-menu/components/release-notes/use-has-read-latest';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/icons';
import { Sheet, SheetContent, SheetDescription } from '@/components/ui/sheet';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { useState } from 'react';

const MAX_VISIBLE_CATEGORIES = 3;

/**
 * Plain text of the first paragraph in a Contentful rich text document,
 * used as a one-line teaser for the release note.
 */
function getFirstParagraphText(document: Document): string | undefined {
  const paragraph = document.content.find(
    (node) => node.nodeType === BLOCKS.PARAGRAPH
  );
  if (!paragraph) return undefined;

  const text = paragraph.content
    .map((node) => (node.nodeType === 'text' ? node.value : ''))
    .join('')
    .trim();

  return text.length > 0 ? text : undefined;
}

export function ReleaseNoteDashboardBanner() {
  const { hasReadLatest, latestNote, markAsRead, isLoading } =
    useReleaseNoteReadStatus();
  const [sheetOpen, setSheetOpen] = useState(false);

  const showBanner = !isLoading && !hasReadLatest && !!latestNote;
  const teaser = latestNote ? getFirstParagraphText(latestNote.content) : null;
  const categoryChips = latestNote?.categories
    .slice(0, MAX_VISIBLE_CATEGORIES)
    .map((category) => (
      <span
        key={category}
        className='rounded-full border border-primary/20 bg-surface px-2 py-px text-xs whitespace-nowrap text-primary dark:text-link'
      >
        {category}
      </span>
    ));

  function handleOpenSheet() {
    markAsRead();
    setSheetOpen(true);
  }

  return (
    <>
      {showBanner && (
        <div className='relative flex w-full animate-in flex-wrap items-center justify-between gap-x-6 gap-y-3 overflow-hidden border-b border-primary/20 bg-card px-6 py-3.5 fade-in slide-in-from-top-2'>
          <div
            aria-hidden
            className='pointer-events-none absolute inset-y-0 left-0 w-[520px] bg-[radial-gradient(520px_140px_at_0%_50%,var(--color-accent),transparent_70%)]'
          />
          <div className='relative flex min-w-0 flex-1 items-center gap-3'>
            <div className='relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-surface text-primary dark:text-link'>
              <Icons.releaseNotes className='size-5' />
              <span className='absolute -top-1 -left-1 flex size-3'>
                <span className='absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75' />
                <span className='relative inline-flex size-3 rounded-full bg-primary' />
              </span>
            </div>
            <div className='flex min-w-0 flex-col gap-0.5'>
              <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                <Badge className='rounded-full px-2 py-0.5'>Nytt</Badge>
                <span className='truncate text-sm font-semibold'>
                  {latestNote.title}
                </span>
                {categoryChips}
              </div>
              {teaser && (
                <span className='truncate text-sm text-muted-foreground'>
                  {teaser}
                </span>
              )}
            </div>
          </div>
          <Button
            variant='default'
            size='sm'
            className='relative'
            onClick={handleOpenSheet}
          >
            Läs om senaste uppdateringen
          </Button>
        </div>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className='flex flex-col gap-4 p-0'>
          <SheetDescription className='sr-only'>
            Release notes gällande hela applikationen
          </SheetDescription>
          <ReleaseNotesSheetContent />
        </SheetContent>
      </Sheet>
    </>
  );
}
